import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Flex,
    Skeleton,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    useDisclosure,
} from '@chakra-ui/react';
import { isServer } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useCustomerGet } from '../../bff-api/customers/useCustomerGet';
import { ConditionalRender } from '../../components/conditionally-render/ConditionalRender';
import { CookingClassesIcon, RecipesIcon } from '../../components/icons';
import { RecommendationLocation } from '../../components/recommendations/RecommendationLocation';
import { AuthType } from '../../contexts/model/AuthType';
import { ProductsAddedToBagDrawer } from '../../contexts/products-added-to-bag-drawer/ProductsAddedToBagDrawer';
import { useGTM } from '../../hooks/gtm/useGTM';
import {
    BR_COOKIE_NAME_IDENTITY,
    useBloomreachCookieGetter,
} from '../../hooks/useBloomreachCookie';
import { useNavigateTo } from '../../hooks/useNavigateTo';
import { useRecommendations } from '../../hooks/useRecommendations';
import { Header, Text } from '../../ui-components';
import { getOriginUrl } from '../../utils/url-utils';
import { SearchHead } from './SearchHead';
import { FilterDrawer } from './components/filters/FilterDrawer';
import { FilterButtons } from './components/filters/filters-buttons/FilterButtons';
import { SearchProductsRelatedCategories } from './components/products-related-categories/SearchProductsRelatedCategories';
import { SearchListing } from './components/search-listing/SearchListing';
import { BloomreachProductType } from './hooks/use-search';
import { useSearches } from './hooks/use-search/useSearches';
import { getSubTitle } from './utils/search-page-utils';
import {
    convertSearchParamsToURLParams,
    convertURLParamsToSearchParams,
} from './utils/search-url-params-utils';

const tabNames = defineMessages({
    products: {
        id: 'search.tabs.products.label',
        defaultMessage: 'Products',
    },
    recipes: {
        id: 'search.tabs.recipes.label',
        defaultMessage: 'Recipes',
    },
    culinary: {
        id: 'search.tabs.classes.label',
        defaultMessage: 'Classes',
    },
    culinary_extended: {
        id: 'search.tabs.culinary_extended.label',
        defaultMessage: 'Cooking Classes',
    },
});

const tabNamesMap = {
    [BloomreachProductType.hardgood]: {
        icon: null,
        title: tabNames.products,
    },
    [BloomreachProductType.recipe]: {
        icon: (
            <RecipesIcon
                data-testid="search-tab-recipes-icon"
                width="16px"
                height="16px"
                margin="3px"
            />
        ),
        title: tabNames.recipes,
    },
    [BloomreachProductType.culinary]: {
        icon: (
            <CookingClassesIcon
                data-testid="search-tab-culinary-icon"
                width="16px"
                height="16px"
                margin="3px"
            />
        ),
        title: tabNames.culinary,
    },
};

const Search: FC = () => {
    const intl = useIntl();
    const location = useLocation();
    const getBrUID2Cookie = useBloomreachCookieGetter(false);
    const brUID2CookieScript = useMemo(() => {
        const brUID2 = getBrUID2Cookie();
        if (!brUID2) return '';
        const expires = new Date();
        expires.setDate(expires.getDate() + 365 * 2);
        const isSurlatableDomain = getOriginUrl().includes('surlatable.com');
        const domain = isSurlatableDomain ? '.surlatable.com' : undefined;
        const cookieScript = [
            `${BR_COOKIE_NAME_IDENTITY}=${encodeURIComponent(brUID2)}`,
            'path=/',
            `expires=${expires.toUTCString()}`,
            ...(domain ? [`domain=${domain}`] : []),
        ];
        return `document.cookie = '${cookieScript.join('; ')}';`;
    }, [getBrUID2Cookie]);
    const history = useHistory();
    const navigate = useNavigateTo();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { updatePageData } = useGTM();
    const { searchesToShow, currentSearch, currentProductType, onChangeQuery } = useSearches();

    const [tabIndex, setTabIndex] = useState(() => {
        // The default value should be evaluated from searchesToShow if SSR did it's job.
        const index = searchesToShow.findIndex(
            ({ productType }) => productType === currentProductType,
        );

        return index >= 0 ? index : 0;
    });
    const [expandedFilters, setExpandedFilters] = useState<number[]>(
        // all filters start closed by default
        [],
    );

    const handleExpandedFiltersChange = useCallback((indices: number | number[]) => {
        if (Array.isArray(indices)) {
            setExpandedFilters(indices);
        }
    }, []);

    // Track which tabs have been revealed to keep their content rendered
    const [revealedTabs, setRevealedTabs] = useState<Set<number>>(() => {
        // Initialize with the current tab index
        const index = searchesToShow.findIndex(
            ({ productType }) => productType === currentProductType,
        );
        return new Set([index >= 0 ? index : 0]);
    });

    const { searchParams, items } = currentSearch;

    useEffect(
        () => () => {
            updatePageData({
                search: undefined,
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    useEffect(() => {
        updatePageData({
            search: {
                products: items,
                params: searchParams,
            },
        });
    }, [updatePageData, searchParams, items]);

    useEffect(() => {
        const index = searchesToShow.findIndex(
            ({ productType }) => productType === currentProductType,
        );

        setTabIndex(index >= 0 ? index : 0);
    }, [currentProductType, searchesToShow]);

    useEffect(() => {
        const handleSearchEvent = (event: CustomEvent<string>): void => {
            const q = event.detail;

            if (q === (currentSearch.searchParams.q || '')) {
                return;
            }

            onChangeQuery(q);
        };

        window.addEventListener('slt-search', handleSearchEvent as EventListener);
        return () => window.removeEventListener('slt-search', handleSearchEvent as EventListener);
    }, [currentSearch.searchParams.q, history, location.pathname, onChangeQuery, searchesToShow]);

    useEffect(() => {
        if (currentSearch.redirectUrl) {
            navigate(currentSearch.redirectUrl, { useWindowLocation: true });
        }
    }, [currentSearch.redirectUrl, navigate]);

    const onChangeTab = useCallback(
        (index: number): void => {
            setTabIndex(index);
            setRevealedTabs((prev) => new Set([...prev, index]));

            const selectedSearch = searchesToShow[index];

            if (selectedSearch) {
                selectedSearch.onSelected();
            }
        },
        [searchesToShow],
    );

    const canonicalUrl = useMemo(() => {
        const urlParams = new URLSearchParams(location.search);
        const searchParamsFromUrl = convertURLParamsToSearchParams({
            urlParams,
            productType:
                (urlParams.get('productType') as BloomreachProductType) ??
                BloomreachProductType.hardgood,
        });
        const params = convertSearchParamsToURLParams({
            q: searchParamsFromUrl.q,
            productType: searchParamsFromUrl.productType,
            page: searchParamsFromUrl.page,
        });
        return `${getOriginUrl()}${location.pathname}?${params.toString()}`;
    }, [location.search, location.pathname]);

    const subTitle = useMemo(
        () => getSubTitle(intl, [currentProductType], currentSearch.total ?? 0),
        [intl, currentSearch.total, currentProductType],
    );

    const onCloseFilterDrawer = useCallback(() => {
        onClose();
    }, [onClose]);

    const onOpenFilterDrawer = useCallback(
        (filterIndex: number) => {
            // -1 means "don't auto-open any filter" (used when clicking All Filters with active selections)
            if (
                filterIndex !== undefined &&
                filterIndex >= 0 &&
                !expandedFilters.includes(filterIndex)
            ) {
                setExpandedFilters((prev) => [...prev, filterIndex]);
            }
            onOpen();
        },
        [onOpen, expandedFilters],
    );

    const { data: customer, isLoading: isCustomerGetLoading } = useCustomerGet();
    const userId =
        customer?.authType === AuthType.REGISTERED ? customer?.customerNo : customer?.customerId;

    const recommendationWidgets = useRecommendations({
        location: RecommendationLocation.SEARCH_RESULTS,
        userId,
    });

    if (isServer && currentSearch.redirectUrl) {
        return <Redirect to={currentSearch.redirectUrl} />;
    }

    return (
        <div className="mx-auto h-full w-full max-w-[1928px]">
            <SearchHead canonicalUrl={canonicalUrl} />
            {brUID2CookieScript ? (
                <Helmet>
                    <script type="text/javascript" data-testid="bloomreach-cookie-script">
                        {brUID2CookieScript}
                    </script>
                </Helmet>
            ) : null}
            <Flex
                data-testid="search-page"
                direction="column"
                paddingX={{ base: 0, sm: 8, lg: 10, xl: 16 }}
                paddingBottom={16}
                paddingTop={4}
            >
                <Flex direction="column" gap={2} paddingX={{ base: 4, sm: 0 }}>
                    <Header
                        id="search.results.for.query"
                        color="slt-pepper.1000"
                        as="h4"
                        variant="header4"
                        style="bold"
                    >
                        <FormattedMessage
                            id="search.results.for.query"
                            defaultMessage='Search Results For "{query}"'
                            values={{ query: searchParams.q }}
                        />
                    </Header>

                    <Text size="small">{subTitle}</Text>
                </Flex>

                <Tabs
                    variant="slt-enclosed"
                    onChange={onChangeTab}
                    index={tabIndex}
                    marginTop={4}
                    data-testid="search-tabs"
                >
                    <TabList marginX={{ sm: -8, lg: -10, xl: -16 }}>
                        {searchesToShow.map((search) => (
                            <Tab
                                data-testid={`${search.productType}-tab`}
                                key={search.productType}
                                paddingX={{ base: 3, sm: 4 }}
                                paddingY={3}
                                textStyle={{ base: 'body-small', sm: 'body-base' }}
                                fontSize={{ base: '0.875rem', sm: '1rem' }}
                            >
                                {search.productType === BloomreachProductType.culinary ? (
                                    <Flex gap={1} alignItems="center">
                                        <ConditionalRender visibility={{ base: true, md: false }}>
                                            <FormattedMessage {...tabNames.culinary} />
                                        </ConditionalRender>
                                        <ConditionalRender visibility={{ base: false, md: true }}>
                                            {tabNamesMap[search.productType].icon}
                                            <FormattedMessage {...tabNames.culinary_extended} />
                                        </ConditionalRender>
                                    </Flex>
                                ) : (
                                    <Flex gap={1} alignItems="center">
                                        <ConditionalRender visibility={{ base: false, md: true }}>
                                            {tabNamesMap[search.productType].icon
                                                ? tabNamesMap[search.productType].icon
                                                : null}
                                        </ConditionalRender>
                                        <FormattedMessage
                                            {...tabNamesMap[search.productType].title}
                                        />
                                    </Flex>
                                )}
                                {search.isFetching &&
                                !(search.isFetchingNextPage || search.isFetchingPreviousPage) ? (
                                    <Skeleton width="30px" height="100%" borderRadius={40} />
                                ) : null}
                                {!search.isPreviousData && search.total ? (
                                    <Box
                                        background="slt-primary-green.100"
                                        borderRadius={40}
                                        paddingY={0.5}
                                        paddingX={1}
                                    >
                                        <Text size="xs">{search.total}</Text>
                                    </Box>
                                ) : null}
                            </Tab>
                        ))}
                    </TabList>
                    <TabPanels>
                        {searchesToShow.map((search, index) => {
                            const isHardgood =
                                search.productType === BloomreachProductType.hardgood;
                            const isCulinary =
                                search.productType === BloomreachProductType.culinary;
                            const isActive = index === tabIndex;
                            const isRevealed = revealedTabs.has(index);

                            return (
                                <TabPanel
                                    data-testid={`${search.productType}-tab-panel`}
                                    key={search.productType}
                                    flexDirection="column"
                                    width="100%"
                                    paddingTop={isHardgood ? undefined : 0}
                                    gap={4}
                                    className="!px-0"
                                >
                                    {isActive || isRevealed ? (
                                        <>
                                            {!isCulinary && (
                                                <FilterButtons
                                                    search={search}
                                                    onOpenFilterDrawer={onOpenFilterDrawer}
                                                />
                                            )}

                                            <SearchListing
                                                id={search.productType}
                                                search={search}
                                                source="SEARCH"
                                            />
                                        </>
                                    ) : null}
                                </TabPanel>
                            );
                        })}
                    </TabPanels>
                </Tabs>

                {/* Add recommendations at the bottom */}
                {!isCustomerGetLoading && recommendationWidgets?.length ? (
                    <div className="mx-4 mt-8 overflow-hidden bg-white sm:mx-0 lg-sm:mx-2 xl:mx-0">
                        <div className="mx-auto flex w-full max-w-[1800px] flex-col gap-8">
                            {recommendationWidgets}
                        </div>
                    </div>
                ) : null}

                <SearchProductsRelatedCategories
                    productType={currentProductType}
                    className="mt-12"
                    query={searchParams.q}
                />
            </Flex>

            <FilterDrawer
                search={currentSearch}
                isOpen={isOpen}
                onClose={onCloseFilterDrawer}
                expandedFilters={expandedFilters}
                onExpandedFiltersChange={handleExpandedFiltersChange}
            />
            <ProductsAddedToBagDrawer />
        </div>
    );
};

export default Search;
